import { useState } from 'react';

import { importarPlanilha } from '../api/participantes';
import type { ResultadoDaImportacao, TurmaDaOpcao } from '../api/participantes';
import { baixarCsv, montarCsvDeCodigos, nomeDoArquivoDeCodigos } from '../importacao';
import { ErroRespostaHttp } from '../../../shared/api/http';
import { JanelaModal } from './JanelaModal';

interface ImportacaoDePlanilhaProps {
    turmas: TurmaDaOpcao[];
    /** A turma do filtro, quando há uma; senão, a primeira ativa. */
    turmaInicial: string;
    csrfToken: string;
    /** Chamado assim que os participantes foram gravados, para a tela reler a lista. */
    aoImportar: (quantidade: number) => void;
    aoFechar: () => void;
}

type Etapa = 'escolher' | 'conferido' | 'importado';
type Andamento = 'conferindo' | 'importando' | null;

/** O arquivo do modelo (`scripts/modelo-participantes.xlsx`), servido pelo próprio painel. */
const ENDERECO_DO_MODELO = '/modelo-participantes.xlsx';

const plural = (quantidade: number, singular: string, mais: string) => (quantidade === 1 ? singular : mais);

/** Do que a API disse para o que a tela mostra: a lista de problemas da planilha ou uma frase. */
function entender(falha: unknown, padrao: string): { problemas: string[]; mensagem: string | null } {
    if (falha instanceof ErroRespostaHttp) {
        const corpo = falha.corpo;
        if (falha.status === 422 && corpo?.codigo === 'planilha_invalida' && Array.isArray(corpo.erros)) {
            return { problemas: corpo.erros.map(String), mensagem: null };
        }
        if (typeof corpo?.mensagem === 'string' && corpo.mensagem) return { problemas: [], mensagem: corpo.mensagem };
    }
    return { problemas: [], mensagem: padrao };
}

/**
 * Importar participantes por planilha: escolhe o `.xlsx`, **confere** (nada é gravado) e só então
 * importa. Os códigos de acesso aparecem uma única vez, ao importar: a tela os mostra e entrega o
 * CSV, e pede confirmação para sair sem baixá-lo.
 */
export function ImportacaoDePlanilha({ turmas, turmaInicial, csrfToken, aoImportar, aoFechar }: ImportacaoDePlanilhaProps) {
    const [turmaId, setTurmaId] = useState(turmaInicial || turmas[0]?.id || '');
    const [arquivo, setArquivo] = useState<File | null>(null);
    const [etapa, setEtapa] = useState<Etapa>('escolher');
    const [resultado, setResultado] = useState<ResultadoDaImportacao | null>(null);
    const [problemas, setProblemas] = useState<string[]>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [andamento, setAndamento] = useState<Andamento>(null);
    const [baixou, setBaixou] = useState(false);
    const [saindo, setSaindo] = useState(false);

    async function enviar(aplicar: boolean) {
        if (andamento || !arquivo || !turmaId) return;
        setErro(null);
        setProblemas([]);
        setAndamento(aplicar ? 'importando' : 'conferindo');
        try {
            const resposta = await importarPlanilha({ turmaId, arquivo, aplicar, csrfToken });
            setResultado(resposta);
            setEtapa(aplicar ? 'importado' : 'conferido');
            if (aplicar) aoImportar(resposta.criados.length);
        } catch (falha) {
            const { problemas: lista, mensagem } = entender(
                falha,
                aplicar ? 'Não foi possível importar os participantes. Nada foi gravado.' : 'Não foi possível ler a planilha. Tente de novo.',
            );
            setProblemas(lista);
            setErro(mensagem);
            // Os problemas da planilha pedem outro arquivo; as demais falhas mantêm a conferência.
            if (lista.length > 0) setEtapa('escolher');
        } finally {
            setAndamento(null);
        }
    }

    function pedirParaFechar() {
        if (etapa === 'importado' && !baixou) {
            setSaindo(true);
            return;
        }
        aoFechar();
    }

    function baixar() {
        if (!resultado) return;
        baixarCsv(nomeDoArquivoDeCodigos(turmaId), montarCsvDeCodigos(resultado.criados));
        setBaixou(true);
        setSaindo(false);
    }

    const ocupado = andamento !== null;
    const novos = resultado?.criados.length ?? 0;

    return (
        <JanelaModal titulo="Importar participantes por planilha" aoFechar={pedirParaFechar} bloqueada={ocupado}>
            <div className="importacao">
                {etapa === 'escolher' && (
                    <form
                        className="formulario-participante"
                        onSubmit={(evento) => {
                            evento.preventDefault();
                            void enviar(false);
                        }}
                    >
                        <p>
                            Envie uma planilha <strong>.xlsx</strong> com <strong>Matrícula</strong> e <strong>Nome</strong>{' '}
                            (obrigatórios), <strong>Grupo</strong> e <strong>Líder</strong> (opcionais), até 300 participantes.{' '}
                            <a href={ENDERECO_DO_MODELO} download>Baixar modelo da planilha</a>
                        </p>
                        <label>
                            Turma
                            <select value={turmaId} onChange={(evento) => setTurmaId(evento.target.value)} disabled={ocupado}>
                                {turmas.map((turma) => (
                                    <option key={turma.id} value={turma.id}>{turma.nome ? `${turma.id} · ${turma.nome}` : turma.id}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Planilha
                            <input
                                type="file"
                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                disabled={ocupado}
                                onChange={(evento) => {
                                    setArquivo(evento.target.files?.[0] ?? null);
                                    setProblemas([]);
                                    setErro(null);
                                }}
                            />
                        </label>
                        {problemas.length > 0 && (
                            <div className="importacao-problemas">
                                <p className="erro-formulario">A planilha tem problemas. Corrija e envie de novo:</p>
                                <ul aria-label="Problemas na planilha">
                                    {problemas.map((problema) => <li key={problema}>{problema}</li>)}
                                </ul>
                            </div>
                        )}
                        {erro && <p className="erro-formulario" role="alert">{erro}</p>}
                        <div className="botoes-confirmacao">
                            <button type="button" className="botao-secundario" onClick={aoFechar} disabled={ocupado}>Cancelar</button>
                            <button type="submit" className="botao-confirmar botao-reativar" disabled={ocupado || !arquivo || !turmaId}>
                                {andamento === 'conferindo' ? 'Conferindo…' : 'Conferir'}
                            </button>
                        </div>
                    </form>
                )}

                {etapa === 'conferido' && resultado && (
                    <>
                        <ul className="importacao-resumo">
                            {novos > 0 ? (
                                <li><strong>{`${novos} ${plural(novos, 'participante será criado', 'participantes serão criados')}.`}</strong></li>
                            ) : (
                                <li><strong>Nenhum participante novo para importar.</strong></li>
                            )}
                            {resultado.ja_existiam.length > 0 && (
                                <li>
                                    {`${resultado.ja_existiam.length} ${plural(resultado.ja_existiam.length, 'já existe na turma e será ignorado', 'já existem na turma e serão ignorados')}.`}
                                </li>
                            )}
                            {resultado.grupos_criados.length > 0 && <li>Grupos novos: {resultado.grupos_criados.join(', ')}.</li>}
                            {resultado.sem_grupo.length > 0 && (
                                <li>{`${resultado.sem_grupo.length} sem grupo: só entram no jogo depois de alocados.`}</li>
                            )}
                            {Object.keys(resultado.lideres).length > 0 && (
                                <li>
                                    Líderes definidos:{' '}
                                    {Object.entries(resultado.lideres).map(([grupo, matricula]) => `${grupo}: ${matricula}`).join('; ')}.
                                </li>
                            )}
                        </ul>
                        {resultado.avisos.length > 0 && (
                            <ul className="importacao-avisos" aria-label="Avisos">
                                {resultado.avisos.map((aviso) => <li key={aviso}>{aviso}</li>)}
                            </ul>
                        )}
                        <p>Nada foi gravado ainda.</p>
                        {erro && <p className="erro-formulario" role="alert">{erro}</p>}
                        <div className="botoes-confirmacao">
                            <button type="button" className="botao-secundario" onClick={() => { setEtapa('escolher'); setErro(null); setArquivo(null); }} disabled={ocupado}>
                                Voltar
                            </button>
                            {novos > 0 && (
                                <button type="button" className="botao-confirmar botao-reativar" onClick={() => void enviar(true)} disabled={ocupado}>
                                    {andamento === 'importando' ? 'Importando…' : `Importar ${novos} ${plural(novos, 'participante', 'participantes')}`}
                                </button>
                            )}
                        </div>
                        {andamento === 'importando' && <p className="aviso-codigo">Isso pode levar alguns segundos. Não feche esta janela.</p>}
                    </>
                )}

                {etapa === 'importado' && resultado && (
                    <>
                        <p>
                            <strong>{`${novos} ${plural(novos, 'participante importado', 'participantes importados')}.`}</strong>
                        </p>
                        <p className="aviso-codigo">
                            Os códigos não aparecem de novo: o servidor guarda só o hash. Baixe o arquivo agora e entregue cada código à pessoa dele.
                        </p>
                        <div className="importacao-tabela">
                            <table aria-label="Códigos de acesso">
                                <thead>
                                    <tr><th>Matrícula</th><th>Nome</th><th>Grupo</th><th>Código</th></tr>
                                </thead>
                                <tbody>
                                    {resultado.criados.map((pessoa) => (
                                        <tr key={pessoa.matricula}>
                                            <td>{pessoa.matricula}</td>
                                            <td>{pessoa.nome}</td>
                                            <td>{pessoa.grupo_id ?? 'Sem grupo'}</td>
                                            <td className="importacao-codigo">{pessoa.codigo}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {saindo && (
                            <div className="importacao-saida" role="alert">
                                <p>Você ainda não baixou os códigos. Sem o arquivo, só gerando um código novo por pessoa.</p>
                                <div className="botoes-confirmacao">
                                    <button type="button" className="botao-secundario" onClick={aoFechar}>Sair sem baixar</button>
                                    <button type="button" className="botao-confirmar botao-reativar" onClick={() => setSaindo(false)}>
                                        Voltar e baixar
                                    </button>
                                </div>
                            </div>
                        )}
                        {!saindo && (
                            <div className="botoes-confirmacao">
                                <button type="button" className="botao-secundario" onClick={baixar}>Baixar CSV</button>
                                <button type="button" className="botao-confirmar" onClick={pedirParaFechar}>Concluir</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </JanelaModal>
    );
}
